// Inicializar Sentry ANTES de cualquier otro import (iter92.D)
// Captura errores que ocurren incluso durante la carga de los módulos.
import "@/instrument";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Importar logger primero para silenciar console.logs en producción
import "@/utils/logger";

import "@/index.css";
import App from "@/App";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/AuthContext";
import { SongsProvider } from "@/contexts/SongsContext";
import { PlaylistsProvider } from "@/contexts/PlaylistsContext";

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SongsProvider>
            <PlaylistsProvider>
              <App />
              <Toaster />
            </PlaylistsProvider>
          </SongsProvider>
        </AuthProvider>
      </BrowserRouter>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  </React.StrictMode>,
);

// ============================================
// SERVICE WORKER Y AUTO-ACTUALIZACIÓN
// ============================================
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      // Registrar Service Worker con estrategia de actualización agresiva
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        updateViaCache: 'none' // Nunca usar cache para el SW
      });
      
      console.log('[SW] Service Worker registrado:', registration.scope);
      
      // Verificar actualizaciones cada 5 minutos (no inmediatamente para evitar loops)
      setTimeout(() => {
        registration.update();
      }, 5000);
      
      setInterval(() => {
        registration.update();
      }, 300000); // 5 minutos
      
      // Detectar cuando hay una nueva versión esperando
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('[SW] Nueva versión del Service Worker encontrada');
        
        newWorker?.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('[SW] Nueva versión instalada y lista');
            // El componente AutoUpdate manejará la UI - NO recargar automáticamente
            window.dispatchEvent(new CustomEvent('sw-update-available'));
          }
        });
      });
      
      // Escuchar mensajes del SW
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATED') {
          console.log('[SW] Recibido mensaje de actualización:', event.data);
          window.dispatchEvent(new CustomEvent('sw-update-available', { detail: event.data }));
        }
      });
      
      // Auto-reload ÚNICO cuando el SW nuevo toma control (por clients.claim en activate).
      // El guard `refreshing` evita loops: solo se recarga una vez por sesión de navegación.
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        console.log('[SW] Nuevo SW activo → recargando página para servir bundle nuevo');
        window.location.reload();
      });
      
      // NO recargar automáticamente en controllerchange - dejar que el usuario decida
      
    } catch (error) {
      console.warn('[SW] Error al registrar Service Worker:', error);
    }
  });
}

// Verificar versión al cargar
window.addEventListener('load', async () => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (response.ok) {
      const data = await response.json();
      console.log('[Version] Versión actual:', data.buildId || data.version);
    }
  } catch (e) {
    // Ignorar errores de versión
  }
});
